import { PageMeta } from "@/components/site/PageMeta";
import { PageHero } from "@/components/site/PageHero";
import { Reveal } from "@/components/site/Reveal";
import { CTABanner } from "@/components/site/CTABanner";
import { PhotoBackground } from "@/components/site/PhotoBackground";
import { CATEGORIES, CONTRACT_VEHICLES, DIFFERENTIATORS, NAICS_CODES, SERVICES } from "@/data/content";

const HERO_IMG =
    "https://images.unsplash.com/photo-1758846946188-12316e71c680?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzOTB8MHwxfHNlYXJjaH3Z2xhc3MlMjBzdGVlbCUyMGZhY2FkZXxlbnwwfHx8MTc5MDk4ODc0N3ww&ixlib=rb-4.1.0&q=85";
const COMPETENCIES_IMG =
    "https://images.unsplash.com/photo-1666036891083-76aa582e381e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2MzR8MHwxfHNlYXJjaHxkZXNlcnQlMjBsYW5kc2NhcGV8ZW58MHx8fHwxNzkwOTg4NzQ3fDA&ixlib=rb-4.1.0&q=85";
const DIFFERENTIATORS_IMG =
    "https://images.unsplash.com/photo-1779700210487-a01758a3c55a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NjZ8MHwxfHNlYXJjaH3Ym9hcmRyb29tJTIwdmlld3xlbnwwfHx8MTc5MDk4ODc0N3ww&ixlib=rb-4.1.0&q=85";
const CODES_IMG =
    "https://images.unsplash.com/photo-1584184200374-73d7f6c6a175?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzN8MHwxfHNlYXJjaH3ncmF5JTIwY29uY3JldGV8ZW58MHx8fHwxNzkwOTg4NzQ3fDA&ixlib=rb-4.1.0&q=85";

const Tag = ({ children, tone = "slate" }) => (
    <span
        className={`inline-block border px-3 py-1 font-mono text-[10px] uppercase tracking-[0.2em] ${
            tone === "amber"
                ? "border-amber-500/70 text-amber-500"
                : "border-slate-700 text-slate-500"
        }`}
    >
        {children}
    </span>
);

const Competencies = () => (
    <section className="relative overflow-hidden border-b border-slate-800/70">
        <PhotoBackground src={COMPETENCIES_IMG} />
        <div className="relative mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-12 lg:py-28">
            <Reveal>
                <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-blue-400">Core Competencies</p>
                <h2 className="mt-5 max-w-2xl font-serif text-3xl font-medium leading-tight tracking-tight text-slate-100 sm:text-4xl lg:text-5xl">
                    What the firm delivers.
                </h2>
            </Reveal>
            <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
                {CATEGORIES.map((cat, i) => (
                    <Reveal key={cat} delay={i * 0.06} className="h-full">
                        <div className="h-full border border-amber-500/70 bg-[#0b0f16] p-8 transition-colors duration-300 hover:border-amber-400 hover:bg-[#0e141d]">
                            <span className="font-mono text-[11px] tracking-[0.25em] text-amber-500">0{i + 1}</span>
                            <h3 className="mt-4 font-serif text-xl font-medium text-slate-100">{cat}</h3>
                            <ul className="mt-6 space-y-3">
                                {SERVICES.filter((s) => s.category === cat).map((s) => (
                                    <li key={s.num} className="border-t border-slate-800/70 pt-3 text-xs leading-relaxed text-slate-400">
                                        {s.title}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </Reveal>
                ))}
            </div>
        </div>
    </section>
);

const Differentiators = () => (
    <section className="relative overflow-hidden border-b border-slate-800/70">
        <PhotoBackground src={DIFFERENTIATORS_IMG} />
        <div className="relative mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-12 lg:py-28">
            <Reveal>
                <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-blue-400">Differentiators</p>
                <h2 className="mt-5 max-w-2xl font-serif text-3xl font-medium leading-tight tracking-tight text-slate-100 sm:text-4xl lg:text-5xl">
                    Why contracting officers shortlist us.
                </h2>
            </Reveal>
            <div className="mt-14 space-y-4">
                {DIFFERENTIATORS.map((d, i) => (
                    <Reveal key={d.title} delay={i * 0.05}>
                        <div data-testid={`differentiator-row-${i + 1}`} className="grid gap-2 border border-amber-500/70 bg-[#0b0f16]/60 px-5 py-8 transition-colors duration-300 hover:border-amber-400 hover:bg-[#0e141d] sm:grid-cols-12 sm:gap-8 sm:px-6">
                            <span className="font-mono text-[11px] tracking-[0.25em] text-amber-500 sm:col-span-2">
                                D.0{i + 1}
                            </span>
                            <h3 className="font-serif text-xl text-slate-100 sm:col-span-4 sm:text-2xl">{d.title}</h3>
                            <p className="text-sm leading-relaxed text-slate-400 sm:col-span-6">{d.body}</p>
                        </div>
                    </Reveal>
                ))}
            </div>
        </div>
    </section>
);

const Codes = () => (
    <section className="relative overflow-hidden border-b border-slate-800/70">
        <PhotoBackground src={CODES_IMG} />
        <div className="relative mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-12 lg:py-28">
            <Reveal>
                <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-blue-400">Procurement Data</p>
                <h2 className="mt-5 max-w-2xl font-serif text-3xl font-medium leading-tight tracking-tight text-slate-100 sm:text-4xl lg:text-5xl">
                    Classification & registration.
                </h2>
                <p className="mt-6 max-w-2xl text-sm leading-relaxed text-slate-500">
                    Codes and identifiers below are published in the structure contracting offices
                    expect. Values marked pending are added only upon verification; the firm does
                    not publish unverified registration data.
                </p>
            </Reveal>

            <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
                {NAICS_CODES.map((n, i) => (
                    <Reveal key={n.code} delay={i * 0.06} className="h-full">
                        <div data-testid={`capabilities-naics-card-${n.code}`} className="flex h-full flex-col border border-amber-500/70 bg-[#0b0f16] p-8 transition-colors duration-300 hover:border-amber-400 hover:bg-[#0e141d]">
                            <span className="font-mono text-3xl tracking-wider text-slate-100">{n.code}</span>
                            <p className="mt-4 flex-1 text-xs leading-relaxed text-slate-400">{n.title}</p>
                            <div className="mt-8">
                                <Tag tone="amber">Pending Verification</Tag>
                            </div>
                        </div>
                    </Reveal>
                ))}
            </div>

            <div className="mt-6 grid gap-6 sm:grid-cols-2">
                {[
                    { label: "Unique Entity Identifier (UEI)", note: "Assigned upon SAM.gov registration" },
                    { label: "CAGE Code", note: "Assigned upon SAM.gov registration" },
                ].map((r, i) => (
                    <Reveal key={r.label} delay={0.1 + i * 0.06} className="h-full">
                        <div data-testid={`capabilities-registration-${i + 1}`} className="flex h-full items-center justify-between gap-6 border border-amber-500/70 bg-[#0b0f16] p-8 transition-colors duration-300 hover:border-amber-400 hover:bg-[#0e141d]">
                            <div>
                                <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-amber-500">{r.label}</p>
                                <p className="mt-3 font-mono text-lg text-slate-300">To be assigned</p>
                                <p className="mt-2 text-xs text-slate-500">{r.note}</p>
                            </div>
                            <Tag tone="amber">Pending</Tag>
                        </div>
                    </Reveal>
                ))}
            </div>
        </div>
    </section>
);

const Vehicles = () => (
    <section className="border-b border-slate-800/70 bg-[#090d14]">
        <div className="mx-auto grid max-w-7xl gap-12 px-4 py-20 sm:px-6 lg:grid-cols-12 lg:px-12 lg:py-28">
            <Reveal className="lg:col-span-4">
                <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-blue-400">Engagement Scope</p>
                <h2 className="mt-5 font-serif text-3xl font-medium leading-tight tracking-tight text-slate-100 sm:text-4xl">
                    Arizona first. National by selection.
                </h2>
                <p className="mt-6 text-sm leading-relaxed text-slate-400">
                    The firm serves collection agencies, debt buyers, and receivables organizations
                    across Arizona, and accepts selective national engagements where the mandate
                    fits the practice.
                </p>
            </Reveal>
            <div className="space-y-4 lg:col-span-8">
                {CONTRACT_VEHICLES.map((v, i) => (
                    <Reveal key={v.title} delay={i * 0.05}>
                        <div data-testid={`vehicle-row-${i + 1}`} className="grid gap-2 border border-amber-500/70 bg-[#0b0f16]/60 px-5 py-6 transition-colors duration-300 hover:border-amber-400 hover:bg-[#0e141d] sm:grid-cols-12 sm:items-baseline sm:gap-8 sm:px-6">
                            <h3 className="font-serif text-xl text-slate-100 sm:col-span-5">{v.title}</h3>
                            <p className="text-sm leading-relaxed text-slate-400 sm:col-span-5">{v.note}</p>
                            <div className="sm:col-span-2 sm:text-right">
                                <Tag>{i < 2 ? "Available" : "Prospective"}</Tag>
                            </div>
                        </div>
                    </Reveal>
                ))}
            </div>
        </div>
    </section>
);

export default function Capabilities() {
    return (
        <>
            <PageMeta
                title="Government Contracting & Capabilities | Nationwide Strategies LLC"
                description="Procurement-ready capabilities statement for public sector engagements: core competencies, differentiators, NAICS classifications, and contracting scope."
            />
            <PageHero
                testid="capabilities-hero"
                kicker="Government Contracting / Capabilities"
                lines={["Procurement-ready,", "verification-first."]}
                lede="A capabilities statement structured for contracting offices: competencies, differentiators, classifications, and registration data published only as verified."
                image={HERO_IMG}
            />
            <Competencies />
            <Differentiators />
            <Codes />
            <Vehicles />
            <CTABanner />
        </>
    );
}
